import { createContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useMutation } from "@apollo/client/react";
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
    setActingRole as setStoreActingRole,
    setActingRoleInLocalStorage,
    getActingRoleFromLocalStorage,
    getRememberRolePreference,
    setRememberRolePreference,
} from '../utils/actingRoleStore';
import { SWITCH_ACTING_ROLE } from '../graphql/resolvers/mutations';
import { PATH_MAIN } from '../routes/paths';
import { UserRole } from 'src/enums/shared.enums';

interface ActingRoleContextValue {
    actingRole: string | null;
    hasMultipleRoles: boolean;
    roles: UserRole[];
    selectedRole: string | null;
    openDialog: () => void;
    closeDialog: () => void;
    switchRole: () => void;
    setSelectedRole: (roleName: UserRole) => void;
    dialogOpen: boolean;
    rememberChoice: boolean;
    setRememberChoice: (remember: boolean) => void;
}

const ActingRoleContext = createContext<ActingRoleContextValue | null>(null);

const ActingRoleProvider = ({ children }: { children: ReactNode }) => {
    const { user, isMultiRole, reload } = useAuth();
    const navigate = useNavigate();

    const currentRole = user?.role?.name ?? null;
    const roles = user?.roles ?? [];
    const hasMultipleRoles = isMultiRole;

    /** Load stored role or default to null */
    const [actingRole, setActingRole] = useState<UserRole | null>(getActingRoleFromLocalStorage());
    const [selectedRole, setSelectedRole] = useState<UserRole | null>(actingRole);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [rememberChoice, setRememberChoice] = useState(false);

    const [switchRoleMutation] = useMutation(SWITCH_ACTING_ROLE);

    /** Helper to call the GraphQL mutation */
    const attemptSwitchRole = useCallback(
        async (roleName: UserRole): Promise<boolean> => {
            try {
                const { data } = await switchRoleMutation({ variables: { roleName } });
                return !!data?.switchRole;
            } catch (err) {
                console.error('Error switching role:', err);
                return false;
            }
        },
        [switchRoleMutation]
    );

    /** Dialog Control */
    const openDialog = useCallback(() => {
        setSelectedRole(actingRole ?? currentRole);
        setRememberChoice(false);
        setDialogOpen(true);
    }, [actingRole, currentRole]);

    const closeDialog = useCallback(() => {
        /** On close, set default role as actingRole if none is set */
        if (!actingRole) {
            handleSetRole(currentRole);
            if (rememberChoice) {
                setRememberRolePreference(true);
            }
        }
        setDialogOpen(false);
    }, [actingRole, currentRole, rememberChoice]);

    /** Helper for setting role locally */
    const handleSetRole = useCallback((roleName: UserRole | null) => {
        setActingRoleInLocalStorage(roleName);
        setStoreActingRole(roleName);
        setActingRole(roleName);
    }, []);

    /** Switch Role */
    const switchRole = useCallback(async () => {
        if (!selectedRole || selectedRole === actingRole) {
            if (rememberChoice) {
                setRememberRolePreference(true);
            }
            return setDialogOpen(false);
        }

        /** Attempt to switch role on the backend first */
        const success = await attemptSwitchRole(selectedRole);
        if (!success) return;

        /** Only update local state if backend switch succeeds */
        handleSetRole(selectedRole);

        if (rememberChoice) {
            setRememberRolePreference(true);
        }

        reload();
        setDialogOpen(false);
        navigate(`${PATH_MAIN.general.dashboard}`);
    }, [selectedRole, actingRole, attemptSwitchRole, rememberChoice]);

    /** Auto-open dialog if user has no stored role and hasn't chosen to remember */
    useEffect(() => {
        if (actingRole || !hasMultipleRoles) return;
        if (getRememberRolePreference()) return;

        openDialog();
    }, [hasMultipleRoles]);

    /** Initialize selectedRole when user loads */
    useEffect(() => {
        if (!currentRole || selectedRole) return;

        setSelectedRole(currentRole);
    }, [currentRole]);

    return (
        <ActingRoleContext.Provider
            value={{
                actingRole,
                roles,
                hasMultipleRoles,
                selectedRole,
                switchRole,
                openDialog,
                closeDialog,
                setSelectedRole,
                dialogOpen,
                rememberChoice,
                setRememberChoice,
            }}
        >
            {children}
        </ActingRoleContext.Provider>
    );
};

export { ActingRoleContext, ActingRoleProvider };
